import { useDispatch, useSelector } from "react-redux";
import { updateConfig } from "../../../store/slices/Report/subscriptionOrdersSlice";
import { updateConfig as globalUpdateConfig } from "../../../store/slices/Global";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getSubscriptionOrdersExportedData } from "../api";
import { getFormatedDate } from "../../../utils/Functions/table";
import {
  useGetSubscriptionOrdersListDataQuery,
  useGetSubscriptionOrdersFilterOptionsQuery,
  useUpdateSubscriptionOrdersTableHeadDataMutation,
} from "../../../store/queries/Report";

const useSubscriptionOrders = () => {
  let paginationOptions = [
    { label: "10 Per page", value: 10 },
    { label: "20 Per page", value: 20 },
    { label: "30 Per page", value: 30 },
  ];

  const dispatch = useDispatch();
  const [isExcelLinkSend, setIsExcelLinkSend] = useState(false);
  const { showEditModal } = useSelector((state) => state.global);
  const subscriptionOrdersState = useSelector(
    (state) => state.subscriptionOrders
  );
  const { data: filterOptions = {} } =
    useGetSubscriptionOrdersFilterOptionsQuery();

  const {
    data: subscriptionOrdersList = {},
    isLoading,
    isFetching,
    refetch,
  } = useGetSubscriptionOrdersListDataQuery({
    page_size: subscriptionOrdersState?.currentPageSize,
    page: subscriptionOrdersState?.currentPage,
    search: subscriptionOrdersState?.search,
    start_date: getFormatedDate(subscriptionOrdersState?.startDate),
    end_date: getFormatedDate(subscriptionOrdersState?.endDate),
    customer_key: subscriptionOrdersState?.storeId,
    plan_id: subscriptionOrdersState?.planId,
    posist_status: subscriptionOrdersState?.status,
    // The listing is a POST, so everything travels in the body - including the sort. The
    // base query only threads sort onto a GET query string, so a sort left out here reaches
    // the service as nothing at all and the column headers become decorative.
    sort: subscriptionOrdersState?.sortBy,
    sort_order: subscriptionOrdersState?.sortOrder,
  });

  const [updateSubscriptionOrdersFields] =
    useUpdateSubscriptionOrdersTableHeadDataMutation();

  const [date, setDate] = useState({
    startDate: subscriptionOrdersState.startDate,
    endDate: subscriptionOrdersState.endDate,
    key: "selection",
  });

  useEffect(() => {
    if (subscriptionOrdersList?.data?.total_count) {
      dispatch(
        updateConfig((state) => {
          state.totalCount = subscriptionOrdersList?.data?.total_count ?? "0";
        })
      );
    }
    //eslint-disable-next-line
  }, [subscriptionOrdersList?.data?.total_count]);

  const handleSearch = (e) => {
    dispatch(
      updateConfig((state) => {
        state.search = e?.target?.value;
        state.currentPage = 1;
      })
    );
  };

  const handleClearClick = () => {
    dispatch(updateConfig((state) => (state.search = "")));
  };

  const handleSort = (label) => {
    if (subscriptionOrdersState?.sortBy === label) {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortOrder =
            subscriptionOrdersState?.sortOrder === "asc" ? "desc" : "asc";
        })
      );
    } else {
      dispatch(
        updateConfig((state) => {
          state.currentPage = 1;
          state.sortBy = label;
          state.sortOrder = "asc";
        })
      );
    }
  };

  const handleEditColumnsClick = () => {
    dispatch(
      globalUpdateConfig((state) => {
        state.showEditModal = !state.showEditModal;
      })
    );
  };

  const getRow = (field, data) => {
    const rows = {
      common: (field, data) => <p className="pro-mb-0">{data[field]}</p>,
    };
    return rows?.[field]?.(field, data) ?? rows["common"](field, data);
  };

  const handlePagination = (page) => {
    dispatch(
      updateConfig((state) => {
        state.currentPage = page;
      })
    );
  };

  const handlePageSize = ({ value: page_size }) => {
    dispatch(
      updateConfig((state) => {
        state.currentPageSize = page_size;
        state.currentPage = 1;
      })
    );
  };

  const handleExcelDownload = () => {
    dispatch(
      updateConfig((state) => {
        state.excelSendModal = !state.excelSendModal;
      })
    );
  };

  const modalClose = () => {
    dispatch(
      updateConfig((state) => {
        state.excelSendModal = false;
      })
    );
  };

  const handleDateChange = (range) => {
    setDate(range);
    dispatch(
      updateConfig((state) => {
        state.currentPage = 1;
        state.startDate = range.startDate;
        state.endDate = range.endDate;
      })
    );
  };

  const handleDateRangeChange = (range) => {
    const startDate = range[0];
    const endDate = range[1];

    setDate({ startDate, endDate });

    dispatch(
      updateConfig((state) => {
        state.currentPage = 1;
        state.startDate = startDate;
        state.endDate = endDate;
      })
    );
  };

  const sendExcelData = async (value) => {
    setIsExcelLinkSend(true);

    // The same filters the table is showing, so the file that arrives is the screen that was
    // asked for rather than the whole collection.
    let params = {
      email: value?.email ?? "",
      start_date: getFormatedDate(subscriptionOrdersState.startDate ?? ""),
      end_date: getFormatedDate(subscriptionOrdersState.endDate ?? ""),
      search: subscriptionOrdersState?.search,
      customer_key: subscriptionOrdersState?.storeId,
      plan_id: subscriptionOrdersState?.planId,
      posist_status: subscriptionOrdersState?.status,
    };

    let response = await getSubscriptionOrdersExportedData(params);

    try {
      if (response?.status === 200) {
        toast.success(`Excel download link send successfully`);
      } else {
        toast.error(`Failed to send download link,Please try again`);
      }
    } catch (error) {
      toast.error(`Failed to send download link`);
    } finally {
      modalClose();
      setIsExcelLinkSend(false);
    }
  };

  const handleChangeStore = (e) => {
    dispatch(
      updateConfig((state) => {
        state.storeId = e?.customer_key ?? "";
        state.currentPage = 1;
      })
    );
  };

  const handleChangePlan = (e) => {
    dispatch(
      updateConfig((state) => {
        state.planId = e?.value ?? "";
        state.currentPage = 1;
      })
    );
  };

  const handleChangeStatus = (e) => {
    dispatch(
      updateConfig((state) => {
        state.status = e?.value ?? "";
        state.currentPage = 1;
      })
    );
  };

  let storeList = filterOptions?.data?.stores?.map((item) => {
    return {
      label: item?.name,
      value: item?._id,
      customer_key: item?.customer_key,
    };
  });

  let planList = filterOptions?.data?.plans?.map((item) => {
    return {
      label: item?.name,
      value: item?._id,
    };
  });

  let statusList = filterOptions?.data?.statuses?.map((item) => {
    return {
      label: item?.name,
      value: item?._id,
    };
  });

  let storeListObj = storeList?.filter(
    (item) => item?.customer_key === subscriptionOrdersState?.storeId
  );
  let planListObj = planList?.filter(
    (item) => item?.value === subscriptionOrdersState?.planId
  );
  let statusListObj = statusList?.filter(
    (item) => item?.value === subscriptionOrdersState?.status
  );

  return {
    date,
    getRow,
    refetch,
    planList,
    storeList,
    isLoading,
    modalClose,
    statusList,
    isFetching,
    planListObj,
    storeListObj,
    handleSearch,
    handleSort,
    statusListObj,
    showEditModal,
    sendExcelData,
    handlePageSize,
    isExcelLinkSend,
    handlePagination,
    handleDateChange,
    handleClearClick,
    handleChangePlan,
    handleChangeStore,
    paginationOptions,
    handleChangeStatus,
    handleExcelDownload,
    handleDateRangeChange,
    subscriptionOrdersList,
    handleEditColumnsClick,
    subscriptionOrdersState,
    updateSubscriptionOrdersFields,
  };
};

export default useSubscriptionOrders;
